import React, { useRef, useState } from 'react';
import { useTheme } from '../theming/default_theme';
import { read } from 'fs';
import axios from 'axios';
import { useEffect } from 'react';
import { post } from '../api/apiService';
import { apiEndPoints } from '../api/apiEndPoints';
import messageBundle from '../assets/constants/message_bundle';
import * as XLSX from 'xlsx';
import { isValidDDMMYYYY } from '../utils/dateUtils';

const FileUploadModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<String | ''>('');
  const [successMessage, setSuccessMessage] = useState('');




  function getCaseInsensitive(row: any, key: string) {
    const foundKey = Object.keys(row).find(k => k.trim().toLowerCase() === key.trim().toLowerCase());
    return foundKey ? row[foundKey] : undefined;
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(''); // Reset error message
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setError('Please upload a valid Excel file (.xlsx or .xls)');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target?.result;
        let workbook;
        if (data instanceof ArrayBuffer) {
          const binaryString = new Uint8Array(data)
            .reduce((acc, byte) => acc + String.fromCharCode(byte), '');
          workbook = XLSX.read(binaryString, { type: 'binary' });
        } else {
          workbook = XLSX.read(data, { type: 'array' });
        }
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        let hasError = false;
        let errorString = '';
        const seenHospitalCodes = new Set();
        const seenRows = new Set();
        let duplicateHospitalCodeFound = false;
        let duplicateRowFound = false;

        const cleanedData = jsonData.map((row: any, idx: number) => {
          // Check for duplicate row
          const rowString = JSON.stringify(row);
          if (seenRows.has(rowString)) {
            // errorString = `Duplicate row found at Row ${idx + 2}`;
            // hasError = true;
            duplicateRowFound = true;
            return null;
          }
          seenRows.add(rowString);

          const obj: any = {};
          const address: any = {};

          const hospitalName = getCaseInsensitive(row, 'Hospital Name');
          if (hospitalName) {
            const hospitalNameStr = String(hospitalName).trim();
            if (hospitalNameStr.length > 100) {
              errorString = 'Hospital Name must not exceed 100 characters. Found: ' + String(hospitalName).trim().length;
              hasError = true;
              return null;
            }
            obj.name = hospitalNameStr;
          } else {
            errorString = 'Hospital Name is required.' + hospitalName;
            hasError = true;
            return null;
          }


          const hospitalCode = getCaseInsensitive(row, 'Hospital ID');
          if (hospitalCode) {
            const hospitalCodeLower = String(hospitalCode).trim().toLowerCase();
            if (hospitalCodeLower.length > 50) {
              errorString = 'Hospital ID must not exceed 50 characters. Found: ' + hospitalCodeLower.length;
              hasError = true;
              return null;
            }
            // Check if hospitalCode is a valid string (alphanumeric and underscores)
            if (!/^[a-zA-Z0-9_]+$/.test(hospitalCodeLower)) {
              errorString = 'Hospital ID must contain only alphanumeric characters and underscores. Found: ' + hospitalCodeLower;
              hasError = true;
              return null;
            }
            obj.hospitalCode = hospitalCodeLower;
            // Check for duplicate hospitalCode (case-insensitive)
            if (seenHospitalCodes.has(hospitalCodeLower)) {
              errorString = `Duplicate Hospital ID found: ${hospitalCode} (Row ${idx + 2})`;
              hasError = true;
              duplicateHospitalCodeFound = true;
              return null;
            }
            seenHospitalCodes.add(hospitalCodeLower);
          } else {
            errorString = 'Hospital Id is required.' + hospitalCode;
            hasError = true;
            return null;
          }

          // ...rest of your mapping logic (location, city, state, etc.)...

          const location = getCaseInsensitive(row, 'Location');
          if (location) {
            const locationStr = String(location).trim();
            if (locationStr.length > 100) {
              errorString = 'Hospital Location must not exceed 100 characters. Found: ' + locationStr.length;
              hasError = true;
              return null;
            }
            address.address1 = locationStr;
          } else {
            errorString = 'Hospital Location is required.' + location;
            hasError = true;
            return null;
          }

          const city = getCaseInsensitive(row, 'City');
          if (city) {
            const cityStr = String(city).trim();
            if (cityStr.length > 50) {
              errorString = 'Hospital City must not exceed 50 characters. Found: ' + city.length;
              hasError = true;
              return null;
            }
            // Check if city is a valid string
            if (!/^[a-zA-Z\s]+$/.test(cityStr)) {
              errorString = 'Hospital City must contain only letters and spaces. Found: ' + city;
              hasError = true;
              return null;
            }
            // Check for any character repeating more than 2 times consecutively
            if (/(.)\1\1+/.test(cityStr)) {
              errorString = 'Invalid city. Found: ' + cityStr;
              hasError = true;
              return null;
            }

            address.city = cityStr;
          }
          else {
            errorString = 'Hospital City is required.' + city;
            hasError = true;
            return null;
          }

          const state = getCaseInsensitive(row, 'State');
          if (state) {
            const stateStr = String(state).trim();
            if (stateStr.length > 50) {
              errorString = 'Hospital State must not exceed 50 characters. Found: ' + state.length;
              hasError = true;
              return null;
            }
            // Check if state is a valid string
            if (!/^[a-zA-Z\s]+$/.test(stateStr)) {
              errorString = 'Hospital State must contain only letters and spaces. Found: ' + state;
              hasError = true;
              return null;
            }

            // Check for any character repeating more than 2 times consecutively
            if (/(.)\1\1+/.test(stateStr)) {
              errorString = 'Invalid state. Found: ' + stateStr;
              hasError = true;
              return null;
            }
            address.state = stateStr;


          }
          else {
            errorString = 'Hospital State is required.' + state;
            hasError = true;
            return null;
          }

          const pincode = getCaseInsensitive(row, 'Pincode');
          if (pincode) {
            const pinStr = String(pincode).trim();
            // Check if pincode is exactly 6 digits
            if (!/^\d{6}$/.test(pinStr)) {
              errorString = 'Hospital Pincode must be a 6-digit number. Found: ' + pinStr;
              hasError = true;
              return null;
            }
            address.pincode = pinStr;
          } else {
            errorString = 'Hospital Pincode is required.' + pincode;
            hasError = true;
            return null;
          }

          obj.services = [];
          const freeScreening = getCaseInsensitive(row, 'Free Screening');
          if (freeScreening && (String(freeScreening).trim().toLowerCase() === 'yes' || String(freeScreening).trim().toLowerCase() === 'no')) {
            if (String(freeScreening).trim().toLowerCase() === 'yes') obj.services.push('Free Screening');
          } else {
            errorString = 'Free Screening data is not provided';
            hasError = true;
            return null;
          }

          const freeSpectacles = getCaseInsensitive(row, 'Free Spectacles');
          if (freeSpectacles && (String(freeSpectacles).trim().toLowerCase() === 'yes' || String(freeSpectacles).trim().toLowerCase() === 'no')) {
            if (String(freeSpectacles).trim().toLowerCase() === 'yes') obj.services.push('Free Spectacles');
          } else {
            errorString = 'Free Spectacles data is not provided';
            hasError = true;
            return null;
          }

          const freeSurgeries = getCaseInsensitive(row, 'Free Surgeries');
          if (freeSurgeries && (String(freeSurgeries).trim().toLowerCase() === 'yes' || String(freeSurgeries).trim().toLowerCase() === 'no')) {
            if (String(freeSurgeries).trim().toLowerCase() === 'yes') obj.services.push('Free Surgeries');
          } else {
            errorString = 'Free Surgery data is not provided';
            hasError = true;
            return null;
          }

          const coordinator = getCaseInsensitive(row, 'Coordinator Name');
          if (coordinator) {
            const coordinatorStr = String(coordinator).trim();
            if (coordinatorStr.length > 50) {
              errorString = 'Hospital Coordinator must not exceed 50 characters. Found: ' + String(coordinator).trim().length;
              hasError = true;
              return null;
            }
            obj.coordinator = coordinatorStr;
          }
          else {
            errorString = 'Hospital Coordinator is required';
            hasError = true;
            return null;
          }

          const coordinatorPhone = getCaseInsensitive(row, 'Coordinator Phone No');
          if (coordinatorPhone) {
            const coordinatorPhoneStr = String(coordinatorPhone).trim();

            // Check: only digits, hyphens, spaces allowed
            if (!/^[\d\s-]+$/.test(coordinatorPhoneStr)) {
              errorString = 'Coordinator Phone must contain only numbers, hyphens, and spaces. Found: ' + coordinatorPhoneStr;
              hasError = true;
              return null;
            }

            // Check: not only spaces or only hyphens
            if (/^[-\s]+$/.test(coordinatorPhoneStr)) {
              errorString = 'Coordinator Phone cannot be only spaces or hyphens. Found: ' + coordinatorPhoneStr;
              hasError = true;
              return null;
            }

            // Check: at least 10 digits
            const digitCount = (coordinatorPhoneStr.match(/\d/g) || []).length;
            if (digitCount < 10) {
              errorString = 'Coordinator Phone must contain at least 10 digits. Found: ' + coordinatorPhoneStr;
              hasError = true;
              return null;
            }

            obj.coordinator_phonenumber = coordinatorPhoneStr;
          }

          const coordinatorEmail = getCaseInsensitive(row, 'Coordinator Email');
          if (coordinatorEmail) {
            const coordinatorEmailStr = String(coordinatorEmail).trim();
            // Check if the email is valid
            const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailPattern.test(coordinatorEmailStr)) {
              errorString = 'Coordinator Email must be a valid email address. Found: ' + coordinatorEmailStr;
              hasError = true;
              return null;
            }
            if (coordinatorEmailStr.length > 100) {
              errorString = 'Coordinator Email must not exceed 100 characters. Found: ' + coordinatorEmailStr.length;
              hasError = true;
              return null;
            }
            // If valid, assign to obj
            obj.coordinator_email = coordinatorEmailStr;
          }

          const googleLink = getCaseInsensitive(row, 'Hospital Website');
          if (googleLink) obj.googleLink = String(googleLink).trim();
          else obj.googleLink = "";

          const status = getCaseInsensitive(row, 'Status');
          if (status) {
            obj.status = String(status).trim() === 'Active' ? true : false;
          } else obj.status = true;

          const hospitalRegistrationDate = getCaseInsensitive(row, 'Registered On');
          if (hospitalRegistrationDate) {
            // console.log("hospitalRegistrationDate-->", hospitalRegistrationDate);

            let regDateStr = String(hospitalRegistrationDate).trim();

            if (!/^\d{2}-\d{2}-\d{4}$/.test(regDateStr)) {
              if (!isNaN(Number(hospitalRegistrationDate)) && hospitalRegistrationDate !== '') {
                regDateStr = excelSerialToDateString(Number(hospitalRegistrationDate) + 1);
              }
              if (!/^\d{2}-\d{2}-\d{4}$/.test(regDateStr)) {
                errorString = 'Registered On must be in DD-MM-YYYY format. Found: ' + regDateStr;
                hasError = true;
                return null;
              }
              else if (!isValidDDMMYYYY(regDateStr)) {
                errorString = 'Registered On must be a valid date in DD-MM-YYYY format. Found: ' + regDateStr;
                hasError = true;
                return null;
              }


            } else if (regDateStr.length === 10) {
              // If it's a Date object, format it to DD-MM-YYYY
              if (hospitalRegistrationDate instanceof Date && !isNaN(hospitalRegistrationDate.getTime())) {
                const d = hospitalRegistrationDate;
                regDateStr = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
              }
              // If it's an Excel serial number, convert to date
              else if (!isNaN(Number(hospitalRegistrationDate)) && (!/^\d{2}-\d{2}-\d{4}$/.test(regDateStr)) && hospitalRegistrationDate !== '') {
                // Excel's epoch starts at 1900-01-01
                const excelEpoch = new Date(1899, 11, 30);
                const days = Number(hospitalRegistrationDate);
                const d = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
                regDateStr = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
                console.log("you are insided here-->", regDateStr);
              }
              // else if (!isNaN(Number(hospitalRegistrationDate)) && hospitalRegistrationDate !== '') {

              // }

              // Now check for DD-MM-YYYY format
              if (!/^\d{2}-\d{2}-\d{4}$/.test(regDateStr)) {
                errorString = 'Registered On must be in DD-MM-YYYY format. Found: ' + regDateStr;
                hasError = true;
                return null;
              }
              else if (!isValidDDMMYYYY(regDateStr)) {
                errorString = 'Registered On must be a valid date in DD-MM-YYYY format. Found: ' + regDateStr;
                hasError = true;
                return null;
              }
            }


            obj.registration_date = regDateStr;
          } else {
            obj.registration_date = '';
          }

          obj.address = address;
          obj.status = true;
          return obj;
        }).filter(obj => obj !== null);

        // If any duplicate found, show error and do not proceed
        if (duplicateHospitalCodeFound) {
          setError(errorString);
          return;
        }

        if ((!hasError) && (cleanedData.length > 0) && (fileInputRef.current)) {
          console.log('cleanedData-->', cleanedData);
          postHospitalsData(cleanedData);
        } else {
          if (hasError && errorString !== '') {
            setError(errorString);
          }
        }
      };

      reader.readAsArrayBuffer(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };



  const postHospitalsData = async (hospitalData: any[]) => {

    if (!hospitalData.length) {
      setError('No data to send');


    }
    else {
      setLoading(true);
      try {
        const url = apiEndPoints.hospitalsBulkUploadEndPoint;
        const response = await post(url, // Replace with your API endpoint
          hospitalData, // Send parsed data as payload
        ); // Replace with your API endpoint
        console.log('API Response:', response);
        if (response && response.includes('Successfully uploaded')) {
          console.log("response-->", response);
          setSuccessMessage('File uploaded successfully!');
          setTimeout(() => {
            setSuccessMessage('');
            window.location.reload();
          }, 3000); // 2 minutes
        }
        else if (response && response.includes('No new hospitals to save')) {

          setError('No new hospitals to save');
        }
        else if (response && response.includes('Hospital with the given codes already exists')) {
          setError('Hospital with the given codes already exists');
        }
        else {
          setError('Failed to upload hospitals data');
        }


      } catch (err) {
        if (typeof err === 'object' && err !== null && 'response' in err) {
          const apiResponse = (err as any).response;
          const apiMessage = apiResponse?.data?.message || JSON.stringify(apiResponse?.data);

          console.error('API Error Response:', apiResponse);

          // Check for "No new hospitals to save" in the backend response
          if (apiMessage && apiMessage.includes('No new hospitals to save')) {
            setError('No new hospitals to save');
          }
          else if (apiMessage && apiMessage.includes('Hospital with the given codes already exists')) {
            setError('Hospital with the given codes already exists');
          }
          else {
            setError('Failed to send data: ' + apiMessage);
          }
        } else if (err instanceof Error) {
          if (err.message.includes('Network Error')) {
            setError('Network Error: Please check your internet connection.');
          } else if (err.message.includes('timeout')) {
            setError('Request timed out. Please try again later.');
          } else if (err.message.includes('Request failed with status code 500')) {
            setError('Internal Server Error: Please try again later.');
          } else if (err.message.includes('Request failed with status code 400')) {
            setError(err.message);
          } else if (err.message.includes('No new hospitals to save')) {
            setError(err.message);
          } else if (err.message.includes('Hospital with the given codes already exists')) {
            setError(err.message);
          } else {
            setError('Failed to upload data: Unknown error');
            console.log('Unknown error', err);
          }
        } else if (typeof err === 'string') {
          setError(err);
        }
        else {
          setError('Failed to upload data: Unknown error');
          console.log('Unknown error', err);
        }
      }
      setLoading(false);
      return;
    }

  }

  function handleButtonClick() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  function excelSerialToDateString(serial: number): string {
    // Excel's leap year bug: 1900 is not a leap year, but Excel treats it as one
    if (serial >= 60) serial -= 1;
    const excelEpoch = Date.UTC(1899, 11, 30); // 1899-12-30 in UTC
    const ms = excelEpoch + serial * 24 * 60 * 60 * 1000;
    const d = new Date(ms);
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${day}-${month}-${year}`;
  }

  return (
    <div className="flex flex-col justify-center items-center fixed top-0 left-0 w-screen h-screen bg-black bg-opacity-50 z-50">
      <div className="flex flex-col bg-white rounded-xl shadow-lg w-1/2 h-1/2 relative ">
        <div className={`flex flex-col block justify-center items-center h-[40%] rounded-xl relative`} style={{ background: 'linear-gradient(to right, #F9F7ED, #FFFFF5, #F5F1E0)' }}>
          <div className="absolute top-4 right-4 flex items-center justify-center w-[3%] h-[15%] cursor-pointer" onClick={onClose}>
            <img src="/close.png" alt="Close" />
          </div>
          <div className="flex flex-col block justify-center items-center gap-y-5">
            <span style={{ fontFamily: theme.typography.fontFamily, fontSize: theme.typography.h1.fontSize, fontWeight: theme.typography.fontWeightBold }}>{messageBundle.fileUploadModal.textMessages.uploadHospitalDataMsg1}</span>
            <span style={{ fontFamily: theme.typography.fontFamily, fontSize: theme.typography.fontSize, fontWeight: theme.typography.fontWeightMedium, color: theme.colors.detailtext }}>{messageBundle.fileUploadModal.textMessages.uploadHospitalDataMsg2}</span>
            <img src="/file.png" alt="File Icon" className="absolute right-9 top-3/4 -translate-y-1 w-[20%] h-[80%]" />
          </div>
        </div>
        <div className="flex flex-col block justify-center items-center h-[60%] bg-white rounded-b-[20px]">
          <span>{messageBundle.fileUploadModal.textMessages.uploadHospitalDataMsg3} </span>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button className={`flex bg-[${theme.colors.surface}] text-white rounded-full h-[15%] p-4 pl-12 pr-12 items-center justify-center mt-10`} onClick={handleButtonClick}>{messageBundle.fileUploadModal.labels.upLoadCSV}</button>
          {/* Error message display */}
          {error && (
            <div className="mt-4 text-red-600 text-xs font-semibold text-center">
              {error}
            </div>
          )}
        </div>
      </div>
      {successMessage && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded shadow-lg z-[9999] text-sm font-semibold animate-fade-in">
          {successMessage}
        </div>
      )}
    </div>

  );
}
export default FileUploadModal;